import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiClient } from '../../shared/api/api';
import { FilterApiModel } from '../../company/models/FilterApi.model';
import { ApiResponse } from '../models/model';
import {
  EcommerceShopModel,
  EcommerceShopCreateRequest,
  EcommerceOrderModel,
  EcommerceOrderItemModel,
  EcommerceOrderNoteModel,
  EcommercePromoCodeModel,
  EcommerceFxRateModel,
  EcommerceDeliveryZoneModel,
  EcommercePickupPointModel,
  EcommerceExtractionPresetModel,
  EcommerceConfigEnableRequest,
  EcommerceUpdateOrderDeliveryRequest,
  EcommerceUpdateItemDeliveryRequest,
  EcommerceUpdateItemQuantityRequest,
  EcommerceBulkItemDeliveryRequest,
  EcommerceDeliveryTimeline
} from '../models/ecommerce.model';

@Injectable({
  providedIn: 'root'
})
export class EcommerceService {
  private baseEndpoint = 'api/admin/ecommerce';
  private shopEndpoint = 'api/admin/application';
  private configEndpoint = 'api/admin/ecommerce/configuration';

  constructor(private apiClient: ApiClient) {}

  // ==================== SHOPS ====================

  /**
   * Get all ecommerce-enabled shops (loads all at once with size=200)
   */
  getEcommerceShops(filter: FilterApiModel): Observable<ApiResponse<EcommerceShopModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceShopModel[]>>(
      `${this.shopEndpoint}?page=1&size=200&query=isEcommerceEnabled==true`
    );
  }

  /**
   * Get shop by ID
   */
  getShopById(id: string): Observable<ApiResponse<EcommerceShopModel>> {
    return this.apiClient.get<ApiResponse<EcommerceShopModel>>(this.shopEndpoint, id);
  }

  updateShop(id: string, payload: any): Observable<any> {
    return this.apiClient.putwithoutId<any>(this.shopEndpoint + '/' + id, payload);
  }

  /**
   * Enable ecommerce for a shop
   */
  enableEcommerce(applicationId: string, request: EcommerceConfigEnableRequest): Observable<ApiResponse<EcommerceShopModel>> {
    let url = `${applicationId}/enable?websiteUrl=${encodeURIComponent(request.websiteUrl)}`;
    if (request.country) {
      url += `&country=${encodeURIComponent(request.country)}`;
    }
    if (request.countryFlag) {
      url += `&countryFlag=${encodeURIComponent(request.countryFlag)}`;
    }
    return this.apiClient.put<ApiResponse<EcommerceShopModel>>(
      `${this.configEndpoint}/applications`,
      url,
      {}
    );
  }

  /**
   * Disable ecommerce for a shop
   */
  disableEcommerce(applicationId: string): Observable<ApiResponse<EcommerceShopModel>> {
    return this.apiClient.put<ApiResponse<EcommerceShopModel>>(
      `${this.configEndpoint}/applications`,
      `${applicationId}/disable`,
      {}
    );
  }

  /**
   * Apply extraction preset to shop
   */
  applyPreset(applicationId: string, presetKey: string): Observable<ApiResponse<EcommerceShopModel>> {
    return this.apiClient.put<ApiResponse<EcommerceShopModel>>(
      `${this.configEndpoint}/applications`,
      `${applicationId}/apply-preset?presetKey=${presetKey}`,
      {}
    );
  }

  // ==================== PRESETS ====================

  /**
   * Get all extraction presets
   */
  getPresets(): Observable<ApiResponse<EcommerceExtractionPresetModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceExtractionPresetModel[]>>(
      `${this.configEndpoint}/presets`
    );
  }

  /**
   * Detect preset for URL
   */
  detectPreset(url: string): Observable<ApiResponse<EcommerceExtractionPresetModel>> {
    return this.apiClient.getAll<ApiResponse<EcommerceExtractionPresetModel>>(
      `${this.configEndpoint}/presets/detect?url=${encodeURIComponent(url)}`
    );
  }

  /**
   * Create new ecommerce shop with fixed merchant "Prime Ecommerce Partners"
   */
  createEcommerceShop(request: {
    name: string;
    websiteUrl: string;
    country?: string;
    countryFlag?: string;
    phone?: string;
    logo?: string;
  }): Observable<ApiResponse<EcommerceShopModel>> {
    return this.apiClient.post<ApiResponse<EcommerceShopModel>>(
      `${this.configEndpoint}/shops`,
      request
    );
  }

  // ==================== ORDERS ====================

  /**
   * Get all orders
   */
  getOrders(filter: FilterApiModel): Observable<ApiResponse<EcommerceOrderModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceOrderModel[]>>(
      `${this.baseEndpoint}/orders?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get orders by status
   */
  getOrdersByStatus(status: string, filter: FilterApiModel): Observable<ApiResponse<EcommerceOrderModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceOrderModel[]>>(
      `${this.baseEndpoint}/orders/status/${status}?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get order by ID
   */
  getOrderById(id: string): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.get<ApiResponse<EcommerceOrderModel>>(`${this.baseEndpoint}/orders`, id);
  }

  /**
   * Update order status
   */
  updateOrderStatus(id: string, status: string): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.put<ApiResponse<EcommerceOrderModel>>(
      `${this.baseEndpoint}/orders`,
      `${id}/status?status=${status}`,
      {}
    );
  }

  /**
   * Assign order to admin
   */
  assignOrder(orderId: string, adminId: string): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.put<ApiResponse<EcommerceOrderModel>>(
      `${this.baseEndpoint}/orders`,
      `${orderId}/assign?adminId=${adminId}`,
      {}
    );
  }

  /**
   * Update order priority
   */
  updateOrderPriority(id: string, priority: string): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.put<ApiResponse<EcommerceOrderModel>>(
      `${this.baseEndpoint}/orders`,
      `${id}/priority?priority=${priority}`,
      {}
    );
  }

  /**
   * Cancel order
   */
  cancelOrder(id: string, reason: string): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.put<ApiResponse<EcommerceOrderModel>>(
      `${this.baseEndpoint}/orders`,
      `${id}/cancel?reason=${encodeURIComponent(reason)}`,
      {}
    );
  }

  /**
   * Update order delivery info (order-level tracking)
   */
  updateOrderDeliveryInfo(id: string, request: EcommerceUpdateOrderDeliveryRequest): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.putwithoutId<ApiResponse<EcommerceOrderModel>>(
      `${this.baseEndpoint}/orders/${id}/delivery`,
      request
    );
  }

  getOrderDeliveryHistory(orderId: string): Observable<ApiResponse<EcommerceDeliveryTimeline>> {
    return this.apiClient.getAll<ApiResponse<EcommerceDeliveryTimeline>>(
      `${this.baseEndpoint}/orders/${orderId}/delivery-history`
    );
  }

  // ==================== ORDER ITEMS ====================

  /**
   * Get order item by ID
   */
  getOrderItemById(id: string): Observable<ApiResponse<EcommerceOrderItemModel>> {
    return this.apiClient.get<ApiResponse<EcommerceOrderItemModel>>(`${this.baseEndpoint}/order-items`, id);
  }

  /**
   * Update item delivery info (item-level tracking)
   */
  updateItemDeliveryInfo(itemId: string, request: EcommerceUpdateItemDeliveryRequest): Observable<ApiResponse<EcommerceOrderItemModel>> {
    return this.apiClient.putwithoutId<ApiResponse<EcommerceOrderItemModel>>(
      `${this.baseEndpoint}/order-items/${itemId}/delivery`,
      request
    );
  }

  /**
   * Update item quantity info (received, issue)
   */
  updateItemQuantityInfo(itemId: string, request: EcommerceUpdateItemQuantityRequest): Observable<ApiResponse<EcommerceOrderItemModel>> {
    return this.apiClient.putwithoutId<ApiResponse<EcommerceOrderItemModel>>(
      `${this.baseEndpoint}/order-items/${itemId}/quantity`,
      request
    );
  }

  /**
   * Bulk update items delivery info
   */
  bulkUpdateItemsDeliveryInfo(request: EcommerceBulkItemDeliveryRequest): Observable<ApiResponse<EcommerceOrderItemModel[]>> {
    return this.apiClient.putwithoutId<ApiResponse<EcommerceOrderItemModel[]>>(
      `${this.baseEndpoint}/order-items/bulk-delivery`,
      request
    );
  }

  // ==================== ORDER NOTES ====================

  /**
   * Get order notes
   */
  getOrderNotes(orderId: string, filter: FilterApiModel): Observable<ApiResponse<EcommerceOrderNoteModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceOrderNoteModel[]>>(
      `${this.baseEndpoint}/orders/${orderId}/notes?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Create order note
   */
  createOrderNote(orderId: string, note: Partial<EcommerceOrderNoteModel>): Observable<ApiResponse<EcommerceOrderNoteModel>> {
    return this.apiClient.post<ApiResponse<EcommerceOrderNoteModel>>(
      `${this.baseEndpoint}/orders/${orderId}/notes`,
      note
    );
  }

  /**
   * Delete order note
   */
  deleteOrderNote(noteId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseEndpoint}/orders/notes`, noteId);
  }

  // ==================== PROMO CODES ====================

  /**
   * Get all promo codes
   */
  getPromoCodes(filter: FilterApiModel): Observable<ApiResponse<EcommercePromoCodeModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommercePromoCodeModel[]>>(
      `${this.baseEndpoint}/promo-codes?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get promo code by ID
   */
  getPromoCodeById(id: string): Observable<ApiResponse<EcommercePromoCodeModel>> {
    return this.apiClient.get<ApiResponse<EcommercePromoCodeModel>>(`${this.baseEndpoint}/promo-codes`, id);
  }

  /**
   * Create promo code
   */
  createPromoCode(promoCode: Partial<EcommercePromoCodeModel>): Observable<ApiResponse<EcommercePromoCodeModel>> {
    return this.apiClient.post<ApiResponse<EcommercePromoCodeModel>>(
      `${this.baseEndpoint}/promo-codes`,
      promoCode
    );
  }

  /**
   * Update promo code
   */
  updatePromoCode(id: string, promoCode: Partial<EcommercePromoCodeModel>): Observable<ApiResponse<EcommercePromoCodeModel>> {
    return this.apiClient.put<ApiResponse<EcommercePromoCodeModel>>(
      `${this.baseEndpoint}/promo-codes`,
      id,
      promoCode
    );
  }

  /**
   * Delete promo code
   */
  deletePromoCode(id: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseEndpoint}/promo-codes`, id);
  }

  // ==================== FX RATES ====================

  /**
   * Get all FX rates
   */
  getFxRates(filter: FilterApiModel): Observable<ApiResponse<EcommerceFxRateModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceFxRateModel[]>>(
      `${this.baseEndpoint}/fx-rates?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get FX rate by ID
   */
  getFxRateById(id: string): Observable<ApiResponse<EcommerceFxRateModel>> {
    return this.apiClient.get<ApiResponse<EcommerceFxRateModel>>(`${this.baseEndpoint}/fx-rates`, id);
  }

  /**
   * Create FX rate
   */
  createFxRate(fxRate: Partial<EcommerceFxRateModel>): Observable<ApiResponse<EcommerceFxRateModel>> {
    return this.apiClient.post<ApiResponse<EcommerceFxRateModel>>(
      `${this.baseEndpoint}/fx-rates`,
      fxRate
    );
  }

  /**
   * Update FX rate
   */
  updateFxRate(id: string, fxRate: Partial<EcommerceFxRateModel>): Observable<ApiResponse<EcommerceFxRateModel>> {
    return this.apiClient.put<ApiResponse<EcommerceFxRateModel>>(
      `${this.baseEndpoint}/fx-rates`,
      id,
      fxRate
    );
  }

  /**
   * Delete FX rate
   */
  deleteFxRate(id: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseEndpoint}/fx-rates`, id);
  }

  /**
   * Refresh all live FX rates from external API
   * This fetches current market rates and updates the database
   */
  refreshLiveRates(): Observable<ApiResponse<EcommerceFxRateModel[]>> {
    return this.apiClient.post<ApiResponse<EcommerceFxRateModel[]>>(
      `${this.baseEndpoint}/fx-rates/refresh-live`,
      {}
    );
  }

  // ==================== DELIVERY ZONES ====================

  /**
   * Get all delivery zones
   */
  getDeliveryZones(filter: FilterApiModel): Observable<ApiResponse<EcommerceDeliveryZoneModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommerceDeliveryZoneModel[]>>(
      `${this.baseEndpoint}/delivery-zones?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get delivery zone by ID
   */
  getDeliveryZoneById(id: string): Observable<ApiResponse<EcommerceDeliveryZoneModel>> {
    return this.apiClient.get<ApiResponse<EcommerceDeliveryZoneModel>>(`${this.baseEndpoint}/delivery-zones`, id);
  }

  /**
   * Create delivery zone
   */
  createDeliveryZone(zone: Partial<EcommerceDeliveryZoneModel>): Observable<ApiResponse<EcommerceDeliveryZoneModel>> {
    return this.apiClient.post<ApiResponse<EcommerceDeliveryZoneModel>>(
      `${this.baseEndpoint}/delivery-zones`,
      zone
    );
  }

  /**
   * Update delivery zone
   */
  updateDeliveryZone(id: string, zone: Partial<EcommerceDeliveryZoneModel>): Observable<ApiResponse<EcommerceDeliveryZoneModel>> {
    return this.apiClient.put<ApiResponse<EcommerceDeliveryZoneModel>>(
      `${this.baseEndpoint}/delivery-zones`,
      id,
      zone
    );
  }

  /**
   * Delete delivery zone
   */
  deleteDeliveryZone(id: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseEndpoint}/delivery-zones`, id);
  }

  // ==================== PICKUP POINTS ====================

  /**
   * Get all pickup points
   */
  getPickupPoints(filter: FilterApiModel): Observable<ApiResponse<EcommercePickupPointModel[]>> {
    return this.apiClient.getAll<ApiResponse<EcommercePickupPointModel[]>>(
      `${this.baseEndpoint}/pickup-points?${filter.getRsqlQuery()}`
    );
  }

  /**
   * Get pickup point by ID
   */
  getPickupPointById(id: string): Observable<ApiResponse<EcommercePickupPointModel>> {
    return this.apiClient.get<ApiResponse<EcommercePickupPointModel>>(`${this.baseEndpoint}/pickup-points`, id);
  }

  /**
   * Create pickup point
   */
  createPickupPoint(point: Partial<EcommercePickupPointModel>): Observable<ApiResponse<EcommercePickupPointModel>> {
    return this.apiClient.post<ApiResponse<EcommercePickupPointModel>>(
      `${this.baseEndpoint}/pickup-points`,
      point
    );
  }

  /**
   * Update pickup point
   */
  updatePickupPoint(id: string, point: Partial<EcommercePickupPointModel>): Observable<ApiResponse<EcommercePickupPointModel>> {
    return this.apiClient.put<ApiResponse<EcommercePickupPointModel>>(
      `${this.baseEndpoint}/pickup-points`,
      id,
      point
    );
  }

  /**
   * Delete pickup point
   */
  deletePickupPoint(id: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.baseEndpoint}/pickup-points`, id);
  }

  // ==================== SETTINGS (GLOBAL CONFIG) ====================

  getGlobalConfigs(): Observable<ApiResponse<any[]>> {
    return this.apiClient.getAll<ApiResponse<any[]>>(
      `${this.configEndpoint}/global-configs`
    );
  }

  updateGlobalConfig(id: string, value: string): Observable<ApiResponse<any>> {
    return this.apiClient.put<ApiResponse<any>>(
      `${this.configEndpoint}/global-configs`,
      id,
      { configValue: value }
    );
  }

  cancelItem(itemId: string, reason: string): Observable<ApiResponse<EcommerceOrderItemModel>> {
    return this.apiClient.putwithoutId<ApiResponse<EcommerceOrderItemModel>>(
      `api/admin/ecommerce/order-items/${itemId}/cancel?reason=${encodeURIComponent(reason)}`,
      {}
    );
  }

  processRefund(orderId: string, paymentMethod: string = 'WAVE'): Observable<ApiResponse<EcommerceOrderModel>> {
    return this.apiClient.post<ApiResponse<EcommerceOrderModel>>(
      `api/admin/ecommerce/orders/${orderId}/refund?paymentMethod=${paymentMethod}`,
      {}
    );
  }
}
